import React, { useState } from 'react';
import { IoSettingsOutline } from 'react-icons/io5';
import { FiUsers, FiToggleLeft, FiMapPin, FiX, FiLink } from 'react-icons/fi';
import { useSettings } from '../hooks/useSettings';
import '../styles/settings.scss';

const SETTINGS_CARDS = [
    {
        id: 'homeContent',
        title: 'محتوى الصفحة الرئيسية',
        description: 'التحكم بالوصف وشروط القبول والمعلومات العامة والمستندات المطلوبة الظاهرة في الصفحة الرئيسية لموقع التقديم',
        icon: <IoSettingsOutline />
    },
    {
        id: 'maxApplications',
        title: 'الحد الأقصى للطلبات',
        description: 'التحكم بأقصى عدد من طلبات التجنيد التي يمكن قبولها',
        icon: <FiUsers />
    },
    {
        id: 'cities',
        title: 'المدن',
        description: 'التحكم بقائمة المدن الظاهرة في نموذج التقديم وفلترة الطلبات',
        icon: <FiMapPin />
    },
    {
        id: 'links',
        title: 'الروابط',
        description: 'التحكم برقم الدعم ورابط صفحة الفيسبوك والموقع الإلكتروني الظاهرة أسفل الصفحة الرئيسية',
        icon: <FiLink />
    }
];

export const Settings = () => {
    const { content, isLoading, isSaving, saveContent } = useSettings();
    const [activeSetting, setActiveSetting] = useState(null);
    const [draft, setDraft] = useState({ description: '', terms: '', generalInfo: '', requiredDocuments: [], confirmationCheckboxes: [], maxApplications: 1500, cities: [], supportPhone: '', facebookUrl: '', websiteUrl: '' });
    const [newCity, setNewCity] = useState('');
    const [newDocument, setNewDocument] = useState('');
    const [newCheckbox, setNewCheckbox] = useState('');

    const openSetting = (id) => {
        setDraft(content);
        setNewCity('');
        setNewDocument('');
        setNewCheckbox('');
        setActiveSetting(id);
    };

    const addCity = () => {
        const trimmed = newCity.trim();
        if (!trimmed || draft.cities.includes(trimmed)) return;
        setDraft((prev) => ({ ...prev, cities: [...prev.cities, trimmed] }));
        setNewCity('');
    };

    const removeCity = (city) => {
        setDraft((prev) => ({ ...prev, cities: prev.cities.filter((c) => c !== city) }));
    };

    const addDocument = () => {
        const trimmed = newDocument.trim();
        if (!trimmed || draft.requiredDocuments.includes(trimmed)) return;
        setDraft((prev) => ({ ...prev, requiredDocuments: [...prev.requiredDocuments, trimmed] }));
        setNewDocument('');
    };

    const removeDocument = (doc) => {
        setDraft((prev) => ({ ...prev, requiredDocuments: prev.requiredDocuments.filter((d) => d !== doc) }));
    };

    const addCheckbox = () => {
        const trimmed = newCheckbox.trim();
        if (!trimmed || draft.confirmationCheckboxes.includes(trimmed)) return;
        setDraft((prev) => ({ ...prev, confirmationCheckboxes: [...prev.confirmationCheckboxes, trimmed] }));
        setNewCheckbox('');
    };

    const removeCheckbox = (item) => {
        setDraft((prev) => ({ ...prev, confirmationCheckboxes: prev.confirmationCheckboxes.filter((c) => c !== item) }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setDraft((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await saveContent(draft);
        setActiveSetting(null);
    };

    const toggleRegistration = () => {
        saveContent({ registrationOpen: !content.registrationOpen });
    };

    const toggleExistingApplicantsOnly = () => {
        saveContent({ existingApplicantsOnly: !content.existingApplicantsOnly });
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <section className="settings-page">
            <div className="settings-grid">
                {SETTINGS_CARDS.map((setting) => (
                    <button
                        key={setting.id}
                        type="button"
                        className="setting-card"
                        onClick={() => openSetting(setting.id)}
                    >
                        <div className="setting-icon">{setting.icon}</div>
                        <h3>{setting.title}</h3>
                        <p>{setting.description}</p>
                    </button>
                ))}

                <div className="setting-card toggle-card">
                    <div className="setting-icon"><FiToggleLeft /></div>
                    <h3>حالة التسجيل</h3>
                    <p>
                        {content.registrationOpen
                            ? 'التسجيل مفتوح حالياً أمام المتقدمين الجدد'
                            : 'التسجيل مغلق حالياً أمام المتقدمين الجدد'}
                    </p>
                    <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                        <input
                            type="checkbox"
                            checked={content.registrationOpen}
                            onChange={toggleRegistration}
                            disabled={isSaving}
                        />
                        <span className="slider"></span>
                    </label>
                </div>

                <div className="setting-card toggle-card">
                    <div className="setting-icon"><FiToggleLeft /></div>
                    <h3>السماح للمتقدمين السابقين فقط</h3>
                    <p>
                        عند إغلاق التسجيل، يتيح هذا الخيار لمن حصل على رمز تحقق مسبقاً إكمال طلبه، بينما يبقى التسجيل مغلقاً أمام أي متقدم جديد
                    </p>
                    <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                        <input
                            type="checkbox"
                            checked={content.existingApplicantsOnly}
                            onChange={toggleExistingApplicantsOnly}
                            disabled={isSaving}
                        />
                        <span className="slider"></span>
                    </label>
                </div>
            </div>

            {activeSetting === 'homeContent' && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>محتوى الصفحة الرئيسية</h2>
                        <p className="hint">هذه النصوص تظهر في الصفحة الرئيسية لموقع التقديم على الوظائف</p>

                        <form onSubmit={handleSubmit}>
                            <div className="input-group">
                                <label>وصف الصفحة الرئيسية</label>
                                <textarea
                                    name="description"
                                    rows={4}
                                    value={draft.description}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="input-group">
                                <label>شروط القبول</label>
                                <textarea
                                    name="terms"
                                    rows={8}
                                    value={draft.terms}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="input-group">
                                <label>معلومات عامة</label>
                                <textarea
                                    name="generalInfo"
                                    rows={6}
                                    value={draft.generalInfo}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="input-group">
                                <label>المستندات المطلوبة</label>
                                <div className="city-tags">
                                    {draft.requiredDocuments.map((doc) => (
                                        <span key={doc} className="city-tag">
                                            {doc}
                                            <button type="button" onClick={() => removeDocument(doc)}>
                                                <FiX />
                                            </button>
                                        </span>
                                    ))}
                                    {draft.requiredDocuments.length === 0 && (
                                        <span className="no-cities">لا توجد مستندات مضافة</span>
                                    )}
                                </div>
                                <div className="add-city-row">
                                    <input
                                        type="text"
                                        value={newDocument}
                                        onChange={(e) => setNewDocument(e.target.value)}
                                        placeholder="اسم المستند"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addDocument();
                                            }
                                        }}
                                    />
                                    <button type="button" className="add-city-btn" onClick={addDocument}>
                                        إضافة
                                    </button>
                                </div>
                            </div>

                            <div className="input-group">
                                <label>مربعات تأكيد التقديم</label>
                                <p className="hint">يجب على المتقدم تحديد جميع هذه المربعات قبل تفعيل زر إرسال الطلب</p>
                                <div className="city-tags">
                                    {draft.confirmationCheckboxes.map((item) => (
                                        <span key={item} className="city-tag">
                                            {item}
                                            <button type="button" onClick={() => removeCheckbox(item)}>
                                                <FiX />
                                            </button>
                                        </span>
                                    ))}
                                    {draft.confirmationCheckboxes.length === 0 && (
                                        <span className="no-cities">لا توجد مربعات تأكيد مضافة</span>
                                    )}
                                </div>
                                <div className="add-city-row">
                                    <input
                                        type="text"
                                        value={newCheckbox}
                                        onChange={(e) => setNewCheckbox(e.target.value)}
                                        placeholder="نص مربع التأكيد"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addCheckbox();
                                            }
                                        }}
                                    />
                                    <button type="button" className="add-city-btn" onClick={addCheckbox}>
                                        إضافة
                                    </button>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="submit" className="save-btn" disabled={isSaving}>
                                    {isSaving ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
                                </button>
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setActiveSetting(null)}
                                    disabled={isSaving}
                                >
                                    إلغاء
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {activeSetting === 'maxApplications' && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>الحد الأقصى للطلبات</h2>
                        <p className="hint">لن يتمكن أي متقدم جديد من التسجيل بعد الوصول لهذا العدد من الطلبات</p>

                        <form onSubmit={handleSubmit}>
                            <div className="input-group">
                                <label>أقصى عدد للطلبات</label>
                                <input
                                    type="number"
                                    name="maxApplications"
                                    min="0"
                                    value={draft.maxApplications}
                                    onChange={(e) => setDraft((prev) => ({ ...prev, maxApplications: Number(e.target.value) }))}
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="submit" className="save-btn" disabled={isSaving}>
                                    {isSaving ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
                                </button>
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setActiveSetting(null)}
                                    disabled={isSaving}
                                >
                                    إلغاء
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {activeSetting === 'cities' && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>المدن</h2>
                        <p className="hint">هذه المدن تظهر في نموذج التقديم وفي فلتر المدينة بلوحة التحكم</p>

                        <form onSubmit={handleSubmit}>
                            <div className="input-group">
                                <label>المدن الحالية</label>
                                <div className="city-tags">
                                    {draft.cities.map((city) => (
                                        <span key={city} className="city-tag">
                                            {city}
                                            <button type="button" onClick={() => removeCity(city)}>
                                                <FiX />
                                            </button>
                                        </span>
                                    ))}
                                    {draft.cities.length === 0 && (
                                        <span className="no-cities">لا توجد مدن مضافة</span>
                                    )}
                                </div>
                            </div>

                            <div className="input-group">
                                <label>إضافة مدينة</label>
                                <div className="add-city-row">
                                    <input
                                        type="text"
                                        value={newCity}
                                        onChange={(e) => setNewCity(e.target.value)}
                                        placeholder="اسم المدينة"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addCity();
                                            }
                                        }}
                                    />
                                    <button type="button" className="add-city-btn" onClick={addCity}>
                                        إضافة
                                    </button>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="submit" className="save-btn" disabled={isSaving}>
                                    {isSaving ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
                                </button>
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setActiveSetting(null)}
                                    disabled={isSaving}
                                >
                                    إلغاء
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {activeSetting === 'links' && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>الروابط</h2>
                        <p className="hint">تظهر هذه الروابط أسفل الصفحة الرئيسية لموقع التقديم، ويُخفى أي حقل يُترك فارغاً</p>

                        <form onSubmit={handleSubmit}>
                            <div className="input-group">
                                <label>رقم الدعم</label>
                                <input
                                    type="text"
                                    name="supportPhone"
                                    dir="ltr"
                                    placeholder="0910000000"
                                    value={draft.supportPhone}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="input-group">
                                <label>رابط صفحة الفيسبوك</label>
                                <input
                                    type="text"
                                    name="facebookUrl"
                                    dir="ltr"
                                    placeholder="https://facebook.com/..."
                                    value={draft.facebookUrl}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="input-group">
                                <label>الموقع الإلكتروني</label>
                                <input
                                    type="text"
                                    name="websiteUrl"
                                    dir="ltr"
                                    placeholder="https://..."
                                    value={draft.websiteUrl}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="submit" className="save-btn" disabled={isSaving}>
                                    {isSaving ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
                                </button>
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setActiveSetting(null)}
                                    disabled={isSaving}
                                >
                                    إلغاء
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Settings;
